import { passwordSchema } from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { ApiError } from '@/lib/api/client.js';
import { toast } from '@/lib/toast.js';
import { resetPassword } from './api.js';

const formSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirmation requise'),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['confirmPassword'],
  });

type ResetPasswordFormValues = z.infer<typeof formSchema>;

export interface ResetPasswordFormProps {
  token: string;
}

/**
 * `02` §1 : réinitialisation et première connexion après invitation partagent
 * ce même formulaire (même jeton, même route `/reinitialiser/:token`).
 */
export function ResetPasswordForm({ token }: ResetPasswordFormProps): JSX.Element {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({ resolver: zodResolver(formSchema) });

  const mutation = useMutation({
    mutationFn: (values: ResetPasswordFormValues) =>
      resetPassword({ token, password: values.password }),
    onSuccess: (result) => {
      toast.success(result.message ?? 'Mot de passe défini.');
      void navigate({ to: '/connexion' });
    },
    onError: (error: unknown) => {
      setError('root', {
        message: error instanceof ApiError ? error.message : 'Une erreur technique est survenue.',
      });
    },
  });

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        void handleSubmit((values) => {
          mutation.mutate(values);
        })(event);
      }}
    >
      <Field label="Nouveau mot de passe" htmlFor="password" error={errors.password?.message}>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.password ? true : undefined}
          {...register('password')}
        />
      </Field>
      <Field label="Confirmation" htmlFor="confirmPassword" error={errors.confirmPassword?.message}>
        <Input
          id="confirmPassword"
          type="password"
          autoComplete="new-password"
          aria-invalid={errors.confirmPassword ? true : undefined}
          {...register('confirmPassword')}
        />
      </Field>
      {errors.root ? (
        <p
          role="alert"
          className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
        >
          {errors.root.message}
        </p>
      ) : null}
      <Button type="submit" className="w-full" isLoading={isSubmitting || mutation.isPending}>
        Définir le mot de passe
      </Button>
    </form>
  );
}
